// src/utils/perUserStorage.js — localStorage that belongs to WHOEVER IS SIGNED IN.
//
// THE BUG THIS EXISTS TO STOP. The generation queue and the notification list were each kept
// under one key for the whole device — `magi_generation_queue`, `magi_notifications` — with no
// uid in the key and none in the entries. Sign out of one account and into another on the same
// machine and the new account rendered the old one's work: a finished generation from the
// previous user sat in the Workspace grid as a black tile that could never go away, because
// the thing that replaces such a tile is its video appearing in YOUR library, and that video
// belongs to somebody else. Reported 2026-10-24.
//
// SCOPED, NOT CLEARED ON SIGN-OUT, and that is deliberate. Clearing at sign-out only works if
// sign-out actually runs — it does not after a crash, a force-quit, or a session that simply
// expires — and it would also throw away your own in-flight generation the moment you switched
// accounts and back. Keying by uid is true whatever happened: another account's entries are
// unreadable rather than merely deleted-on-a-good-day.
//
// Every value is wrapped as `{ at, v }`. `at` is what lets another account's leftovers be
// swept off the device after a while without having to know anything about the payload.

const stamp = (value) => JSON.stringify({ at: Date.now(), v: value });

export const userKey = (prefix, userId) => `${prefix}:${userId}`;

/** Read this user's value, or `fallback` when there is nothing usable stored. */
export function readUserJSON(prefix, userId, fallback = null) {
  if (!userId) return fallback;
  try {
    const raw = localStorage.getItem(userKey(prefix, userId));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    // The envelope is the only shape this writes. Anything else is from before the keys were
    // scoped, which means it cannot be attributed to anyone — see `dropUnscoped`.
    return parsed && typeof parsed === "object" && "v" in parsed ? parsed.v : fallback;
  } catch {
    return fallback;            // private mode, quota, corrupt value — never throw at a caller
  }
}

/** Write this user's value. A no-op when nobody is signed in, so a signed-out tab cannot
 *  leave anything behind for the next person. */
export function writeUserJSON(prefix, userId, value) {
  if (!userId) return;
  try {
    localStorage.setItem(userKey(prefix, userId), stamp(value));
  } catch {
    // Storage full or unavailable. The in-memory copy still works for this page load, which
    // is what this had before it was persisted at all.
  }
}

export function clearUserJSON(prefix, userId) {
  if (!userId) return;
  try {
    localStorage.removeItem(userKey(prefix, userId));
  } catch { /* ignore */ }
}

/** Delete the pre-scoping key. Its contents belong to SOMEONE, and there is no way to find out
 *  who — so the only honest thing to do with it is drop it. The cost is that a generation that
 *  was in flight across the deploy loses its tile; the job still finishes and the video still
 *  lands in the library it belongs to. */
export function dropUnscoped(prefix) {
  try {
    localStorage.removeItem(prefix);
  } catch { /* ignore */ }
}

/** Sweep other accounts' buckets once they are older than `maxAgeMs`.
 *
 *  Not on every write — scanning localStorage is cheap but not free, and the only moment the
 *  set of buckets can meaningfully change is when the signed-in user changes. */
export function pruneForeign(prefix, userId, maxAgeMs) {
  try {
    const mine = userId ? userKey(prefix, userId) : null;
    const now = Date.now();
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || key === mine || !key.startsWith(`${prefix}:`)) continue;
      let at = 0;
      try {
        at = JSON.parse(localStorage.getItem(key) || "{}").at || 0;
      } catch {
        at = 0;                 // unreadable: treat as ancient and sweep it
      }
      if (now - at > maxAgeMs) doomed.push(key);
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch { /* ignore */ }
}

/** Move one store from a uid to another, merging into whatever the destination already has.
 *
 *  FOR THE ANONYMOUS HAND-OVER, and only that. Signing in normally keeps the uid (every path
 *  in auth.js links onto the anonymous user), so nothing moves. The exception is signing into
 *  an account that already exists: linking is impossible, the uid genuinely changes, and the
 *  backend migrates the anonymous work over — `claimAnonVideos`. Without this the client half
 *  stayed behind, so a video that was generating at the moment you signed in lost its tile
 *  even though the video itself followed you.
 *
 *  `idOf` returns the identity to dedupe on; entries already at the destination win, because
 *  they are the more recent truth for that account.
 */
export function handOverUserJSON(prefix, fromUid, toUid, { idOf = null, max = 200 } = {}) {
  if (!fromUid || !toUid || fromUid === toUid) return;
  const incoming = readUserJSON(prefix, fromUid, null);
  if (!Array.isArray(incoming) || incoming.length === 0) {
    clearUserJSON(prefix, fromUid);
    return;
  }
  const existing = readUserJSON(prefix, toUid, []) || [];
  let merged;
  if (idOf) {
    const have = new Set(existing.map(idOf).filter(Boolean));
    merged = [...existing, ...incoming.filter((item) => !have.has(idOf(item)))];
  } else {
    merged = [...existing, ...incoming];
  }
  writeUserJSON(prefix, toUid, merged.slice(0, max));
  clearUserJSON(prefix, fromUid);
}
